import { useDebounce, useErrorAlert } from "@commercekit/react";
import { useCheckoutAddress, useLocale } from "@commercekit/remix";
import type {
  FindItem,
  LoqateVerifyMatch,
  LoqateVerifyResponse,
  RetrieveItem,
  UseLoqateReturn,
  VerifyAddressInput,
} from "@commercekit/shared";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

const BASE_SEARCH_URL = "https://api.addressy.com/Capture/Interactive";
const BASE_VERIFY_URL =
  "https://api.addressy.com/Cleansing/International/Batch/v1.10/json6.ws";

export const useLoqate = (
  apiKeys?: Record<string, string>
): UseLoqateReturn => {
  const { locale } = useLocale()
  const { isManualAddress } = useCheckoutAddress();
  const { show } = useErrorAlert();
  const [countryCode, setCountryCode] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<Record<string, string>>({
    billing_address_search: "",
    shipping_address_search: "",
  });
  const [activeLookupField, setActiveLookupField] = useState<
    "billing_address_search" | "shipping_address_search" | ""
  >("");
  const [suggestions, setSuggestions] = useState<FindItem[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeContainer, setActiveContainer] = useState<string | undefined>();
  // State for address verification
  const [isVerifying, setIsVerifying] = useState(false);
  const [bypassValidation, setBypassValidation] = useState(false);

  // State for address suggestions - now accepts array of responses
  const [suggestionModalData, setSuggestionModalData] = useState<
    LoqateVerifyResponse[] | null
  >(null);

  const updateSearchTerm = (name: string, value: string) => {
    setActiveLookupField(name);
    setSearchTerm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  // Check for keys AFTER all hooks are called
  const hasValidKeys =
    apiKeys && apiKeys.loqateSearchApiKey && apiKeys.loqateVerifyApiKey;

  if (!hasValidKeys) {
    console.warn(
      "Loqate API keys not available, hooks initialized but functions disabled"
    );
  }
  const { loqateSearchApiKey, loqateVerifyApiKey } = apiKeys || {};
  const findAddress = useCallback(
    async (text: string, containerId?: string) => {
      if (!text || text.length < 2) {
        setSuggestions([]);
        return;
      }
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams({
        Key: loqateSearchApiKey,
        Text: text,
        Countries: countryCode,
        Language: locale,
      });

      if (containerId) {
        params.append("Container", containerId);
      }

      try {
        const response = await fetch(
          `${BASE_SEARCH_URL}/Find/v1.10/json6.ws?${params.toString()}`
        );
        if (!response.ok) throw new Error("Failed to fetch suggestions.");
        const data = await response.json();
        setSuggestions(
          data.Items.sort((a: FindItem, b: FindItem) => {
            const descA = a.Description || '';
            const descB = b.Description || '';
            const descCompare = descA.localeCompare(descB);
            return descCompare !== 0 ? descCompare : a.Text.localeCompare(b.Text);
          }) || []
        );
      } catch (e: any) {
        setError(e.message);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [loqateSearchApiKey, countryCode]
  );

  const retrieveAddress = useCallback(
    async (name: string, id: string): Promise<RetrieveItem | null> => {
      setIsLoading(true);
      setError(null);
      const params = new URLSearchParams({
        Key: loqateSearchApiKey,
        Id: id,
      });

      try {
        const response = await fetch(
          `${BASE_SEARCH_URL}/Retrieve/v1.20/json6.ws?${params.toString()}`
        );
        if (!response.ok) throw new Error("Failed to retrieve address.");
        const data = await response.json();
        return data.Items && data.Items.length > 0 ? data.Items[0] : null;
      } catch (e: any) {
        setError(e.message);
        return null;
      } finally {
        setIsLoading(false);
        setSuggestions([]);
        updateSearchTerm(name, "");
      }
    },
    [loqateSearchApiKey]
  );

  const verifyAddress = useCallback(
    async (
      Addresses: VerifyAddressInput[]
    ): Promise<LoqateVerifyResponse[] | null> => {
      setIsVerifying(true);
      const body = JSON.stringify({
        Key: loqateVerifyApiKey,
        GeoCode: false,
        Options: {
          Process: "Verify",
        },
        Addresses,
      });

      try {
        const response = await fetch(BASE_VERIFY_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
        });
        if (!response.ok) throw new Error("Failed to verify address.");
        const data = await response.json();
        if (!data.length) return null;

        return data.map((item: any, index: number) => {
          // Get all matches, not just the first one
          const matches = item.Matches || [];
          const aVC = matches[0]?.AVC ?? "";
          const verifiedStatusCodes = ["V", "P", "A"]

          // The score comes from the first match (the best match)
          const score =
            matches.length > 0
              ? Number(aVC.split("-").slice(-1))
              : 0;

          return {
            isVerified: verifiedStatusCodes.includes(aVC.charAt(0)),
            score,
            suggestions: matches, // Return all suggestions
            addressType: index === 0 ? "billing" : "shipping",
          };
        });
      } catch (e: any) {
        show(e.message, "danger");
        return null;
      } finally {
        // Always clear the verifying flag so the checkout button can never get
        // stuck disabled if verification throws or returns early.
        setIsVerifying(false);
      }
    },
    [loqateVerifyApiKey, countryCode]
  );

  const debouncedFind = useDebounce(() => {
    if (searchTerm[activeLookupField]?.length >= 2) {
      findAddress(searchTerm[activeLookupField]);
    }
  }, 300);

  useEffect(() => {
    if (searchTerm[activeLookupField]?.length >= 2) {
      setSuggestions([]);
      setActiveContainer(undefined);
    }
  }, [searchTerm, activeLookupField]);

  useEffect(() => {
    debouncedFind();
  }, [searchTerm, activeLookupField]);

  useEffect(() => {
    if (activeContainer) {
      findAddress(searchTerm[activeLookupField], activeContainer);
    }
  }, [activeContainer])

  useEffect(() => {
    if (isManualAddress) {
      setSearchTerm({});
      setActiveLookupField("");
      setSuggestions([]);
      setIsLoading(false);
      setError(null);
      setActiveContainer(undefined);
    }
  }, [isManualAddress]);

  // Reset bypass flag when any address fields change
  useEffect(() => {
    const form = document.getElementById(
      "checkoutForm"
    ) as HTMLFormElement | null;
    if (!form || !isManualAddress) return;

    const fieldNames = [
      "billing_address1",
      "billing_houseNumber",
      "billing_additionalStreetInfo",
      "billing_city",
      "billing_postalCode",
      "shipping_address1",
      "shipping_houseNumber",
      "shipping_additionalStreetInfo",
      "shipping_city",
      "shipping_postalCode",
    ];

    const handlers: Array<() => void> = [];

    fieldNames.forEach((name) => {
      const el = form.querySelector<HTMLElement>(`[name="${name}"]`);
      if (!el) return;
      const handler = () => setBypassValidation(false);
      handlers.push(handler);
      el.addEventListener("change", handler as EventListener);
      el.addEventListener("input", handler as EventListener);
    });

    return () => {
      fieldNames.forEach((name, idx) => {
        const el = form.querySelector<HTMLElement>(`[name="${name}"]`);
        const handler = handlers[idx];
        if (el && handler) {
          el.removeEventListener("change", handler as EventListener);
          el.removeEventListener("input", handler as EventListener);
        }
      });
    };
  }, []);

  // Function to apply selected suggestion to form fields
  const applySuggestion = useCallback(
    (suggestion: LoqateVerifyMatch, addressType: "billing" | "shipping") => {
      const form = document.getElementById("checkoutForm") as HTMLFormElement;
      if (!form) return;

      // Map Loqate fields to form fields. WOV-1840: include the country so a
      // suggestion from a different country than the dropdown (e.g. a Belgian
      // verify-result on the .NL site) updates countryCode rather than leaving
      // a country/address mismatch that ships to NetSuite as the wrong store.
      const fieldMappings = {
        [`${addressType}_address1`]: [
          // Prepend Building/BuildingName | Company when present (helps weak suggestions)
          suggestion.Building || suggestion.BuildingName || suggestion.Company || "",
          suggestion.Thoroughfare || suggestion.Address1,
        ]
          .filter(Boolean)
          .join(" ")
          .trim(),
        [`${addressType}_houseNumber`]:
          suggestion.PremiseNumber || suggestion.Premise,
        [`${addressType}_additionalStreetInfo`]:
          suggestion.SubBuildingNumber || suggestion.SubBuilding,
        [`${addressType}_city`]: suggestion.Locality,
        [`${addressType}_postalCode`]: suggestion.PostalCode.replace(
          /\s+/g,
          ""
        ),
        [`${addressType}_countryCode`]: suggestion["ISO3166-2"]?.toUpperCase(),
      };

      // Apply values to form fields
      Object.entries(fieldMappings).forEach(([fieldName, value]) => {
        const field = form.querySelector(
          `[name="${fieldName}"]`
        ) as HTMLInputElement;
        if (field && value) {
          field.value = value;
          field.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    },
    []
  );

  return {
    setCountryCode,
    searchTerm,
    updateSearchTerm,
    activeLookupField,
    suggestions,
    setSuggestions,
    isLoading,
    error,
    setActiveContainer,
    retrieveAddress,
    verifyAddress,
    isVerifying,
    setIsVerifying,
    suggestionModalData,
    setSuggestionModalData,
    bypassValidation,
    setBypassValidation,
    applySuggestion,
  };
};

const LoqateContext = createContext<UseLoqateReturn | null>(null);

export const LoqateProvider = ({
  children,
  apiKeys,
}: {
  children: React.ReactNode;
  apiKeys: {
    loqateSearchApiKey: string;
    loqateVerifyApiKey: string;
  };
}) => {
  const value = useLoqate(apiKeys);

  return (
    <LoqateContext.Provider value={value}>{children}</LoqateContext.Provider>
  );
};

export const useLoqateContext = () => {
  try {
    const context = useContext(LoqateContext);
    if (!context) {
      // Provider not available - return safe defaults
      return {
        setCountryCode: () => {},
        searchTerm: {},
        updateSearchTerm: () => {},
        activeLookupField: "",
        suggestions: [],
        setSuggestions: () => {},
        isLoading: false,
        error: null,
        setActiveContainer: () => {},
        retrieveAddress: async () => null,
        verifyAddress: async () => null,
        isVerifying: false,
        setIsVerifying: () => {},
        suggestionModalData: null,
        setSuggestionModalData: () => {},
        bypassValidation: false,
        setBypassValidation: () => {},
        applySuggestion: () => {},
      };
    }
    return context;
  } catch (error) {
    // Catch any context-related errors during unmounting
    console.warn("LoqateContext error:", error);
    return {
      setCountryCode: () => {},
      searchTerm: {},
      updateSearchTerm: () => {},
      activeLookupField: "",
      suggestions: [],
      setSuggestions: () => {},
      isLoading: false,
      error: null,
      setActiveContainer: () => {},
      retrieveAddress: async () => null,
      verifyAddress: async () => null,
      isVerifying: false,
      setIsVerifying: () => {},
      suggestionModalData: null,
      setSuggestionModalData: () => {},
      bypassValidation: false,
      setBypassValidation: () => {},
      applySuggestion: () => {},
    };
  }
};
